import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Loader2, Stamp, CheckCircle2, Truck, Package, Trash2, Plus, Save,
  Copy as CopyIcon, ExternalLink, CircleDollarSign, Mail, MessageCircle, AlertCircle, UserPlus,
} from 'lucide-react';
import { SelloCargaManualDialog } from '../components/sellos/SelloCargaManualDialog';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { useAuth } from '../contexts/AuthContext';
import { toast, Toaster } from 'sonner';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ESTADO_META = {
  solicitado: { label: 'Solicitado', color: 'bg-slate-100 text-slate-700' },
  pago_pendiente_colegio: { label: 'A pagar', color: 'bg-amber-100 text-amber-800' },
  pagado: { label: 'Pagado', color: 'bg-blue-100 text-blue-800' },
  en_produccion: { label: 'En producción', color: 'bg-purple-100 text-purple-800' },
  listo_retiro: { label: 'Listo retiro', color: 'bg-green-100 text-green-800' },
  entregado: { label: 'Entregado', color: 'bg-slate-200 text-slate-700' },
};

const TABS = [
  { id: 'solicitado', label: 'Solicitados (esperando validación)', icon: Stamp },
  { id: 'pago_pendiente_colegio', label: 'Validados — a pagar', icon: CircleDollarSign },
  { id: 'pagado', label: 'Pagados', icon: CheckCircle2 },
  { id: 'en_produccion', label: 'En producción', icon: Package },
  { id: 'listo_retiro', label: 'Listos', icon: Truck },
  { id: 'entregado', label: 'Entregados', icon: CheckCircle2 },
  { id: '', label: 'Todos', icon: Stamp },
];

export default function AdminSellosPage() {
  const { token } = useAuth();
  const [tab, setTab] = useState('pago_pendiente_colegio');
  const [pedidos, setPedidos] = useState([]);
  const [proveedores, setProveedores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [seleccion, setSeleccion] = useState(new Set());
  const [marcandoPagados, setMarcandoPagados] = useState(false);
  const [showProveedores, setShowProveedores] = useState(false);
  const [showCargaManual, setShowCargaManual] = useState(false);
  const [nuevoProv, setNuevoProv] = useState({ nombre: '', direccion: '', telefono: '', pin_confirmacion: '', activo: true, es_default: false });

  const headers = { Authorization: `Bearer ${token}` };

  const cargar = async () => {
    setLoading(true);
    try {
      const url = tab ? `${API_URL}/admin/sellos/pedidos?estado=${tab}` : `${API_URL}/admin/sellos/pedidos`;
      const [rP, rProv] = await Promise.all([
        axios.get(url, { headers }),
        axios.get(`${API_URL}/admin/sellos/proveedores`, { headers }),
      ]);
      setPedidos(rP.data.pedidos || []);
      setProveedores(rProv.data.proveedores || []);
      setSeleccion(new Set());
    } catch (err) {
      toast.error('Error cargando pedidos: ' + (err.response?.data?.detail || err.message));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { cargar(); /* eslint-disable-next-line */ }, [tab]);

  const toggleSel = (id) => {
    const s = new Set(seleccion);
    if (s.has(id)) s.delete(id); else s.add(id);
    setSeleccion(s);
  };

  const marcarPagados = async () => {
    if (seleccion.size === 0) {
      toast.error('Seleccioná al menos un pedido.');
      return;
    }
    if (!window.confirm(`¿Confirmás marcar ${seleccion.size} pedido(s) como PAGADO?`)) return;
    setMarcandoPagados(true);
    try {
      const r = await axios.post(
        `${API_URL}/admin/sellos/pedidos/marcar-pagados`,
        { ids: Array.from(seleccion) },
        { headers },
      );
      toast.success(`${r.data.modificados} pedido(s) marcados como pagados`);
      await cargar();
    } catch (err) {
      toast.error('Error: ' + (err.response?.data?.detail || err.message));
    } finally {
      setMarcandoPagados(false);
    }
  };

  const copiarLink = (accessToken) => {
    const url = `${window.location.origin}/sello/${accessToken}`;
    navigator.clipboard.writeText(url);
    toast.success('Link del proveedor copiado');
  };

  const crearProveedor = async () => {
    if (!nuevoProv.nombre || !nuevoProv.direccion) {
      toast.error('Nombre y dirección son obligatorios');
      return;
    }
    const pin = (nuevoProv.pin_confirmacion || '').trim();
    if (pin && !/^\d{4}$/.test(pin)) {
      toast.error('El PIN debe ser exactamente 4 dígitos numéricos');
      return;
    }
    try {
      await axios.post(`${API_URL}/admin/sellos/proveedores`, { ...nuevoProv, pin_confirmacion: pin || null }, { headers });
      toast.success('Proveedor creado');
      setNuevoProv({ nombre: '', direccion: '', telefono: '', pin_confirmacion: '', activo: true, es_default: false });
      await cargar();
    } catch (err) {
      toast.error('Error: ' + (err.response?.data?.detail || err.message));
    }
  };

  const guardarProveedor = async (id, cambios) => {
    const pin = (cambios.pin_confirmacion ?? '').toString().trim();
    if (pin && !/^\d{4}$/.test(pin)) {
      toast.error('El PIN debe ser exactamente 4 dígitos numéricos');
      return false;
    }
    try {
      await axios.patch(
        `${API_URL}/admin/sellos/proveedores/${id}`,
        { ...cambios, pin_confirmacion: pin || null },
        { headers }
      );
      toast.success('Proveedor guardado');
      await cargar();
      return true;
    } catch (err) {
      toast.error('Error: ' + (err.response?.data?.detail || err.message));
      return false;
    }
  };

  const editarProveedor = async (p, campo, valor) => {
    // Compat: usado por toggles (Activo / Default) — guardado inmediato
    try {
      await axios.patch(`${API_URL}/admin/sellos/proveedores/${p.id}`, { [campo]: valor }, { headers });
      await cargar();
    } catch (err) {
      toast.error('Error: ' + (err.response?.data?.detail || err.message));
    }
  };

  const borrarPedido = async (pedido) => {
    const nombre = `${pedido.nombre || ''} ${pedido.apellido || ''}`.trim() || pedido.matricula || pedido.id;
    if (!window.confirm(`¿Borrar definitivamente el pedido de ${nombre}? Esta acción no se puede deshacer.`)) return;
    try {
      await axios.delete(`${API_URL}/admin/sellos/pedidos/${pedido.id}`, { headers });
      toast.success('Pedido eliminado');
      await cargar();
    } catch (err) {
      toast.error('Error: ' + (err.response?.data?.detail || err.message));
    }
  };

  const [reenviando, setReenviando] = useState(null);
  const reenviarQr = async (pedido) => {
    if (!pedido.email) {
      toast.error('El pedido no tiene email registrado');
      return;
    }
    setReenviando(pedido.id);
    try {
      await axios.post(`${API_URL}/admin/sellos/pedidos/${pedido.id}/reenviar-qr`, {}, { headers });
      toast.success(`QR reenviado a ${pedido.email}`);
      await cargar();
    } catch (err) {
      toast.error('Error: ' + (err.response?.data?.detail || err.message));
    } finally {
      setReenviando(null);
    }
  };

  const [notificandoProv, setNotificandoProv] = useState(null);
  const notificarProveedor = async (pedido) => {
    setNotificandoProv(pedido.id);
    try {
      const r = await axios.post(
        `${API_URL}/admin/sellos/pedidos/${pedido.id}/notificar-proveedor`,
        {},
        { headers },
      );
      toast.success(r.data?.mensaje || 'Notificación WhatsApp enviada al proveedor');
      await cargar();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'No se pudo notificar al proveedor');
    } finally {
      setNotificandoProv(null);
    }
  };

  const borrarProveedor = async (p) => {
    if (!window.confirm(`¿Borrar proveedor "${p.nombre}"?`)) return;
    try {
      await axios.delete(`${API_URL}/admin/sellos/proveedores/${p.id}`, { headers });
      toast.success('Proveedor eliminado');
      await cargar();
    } catch (err) {
      toast.error('Error: ' + (err.response?.data?.detail || err.message));
    }
  };

  return (
    <div className="p-4 sm:p-6 max-w-7xl mx-auto space-y-6">
      <Toaster position="top-right" />
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-slate-800 flex items-center gap-2">
            <Stamp className="w-6 h-6 text-purple-700" /> Gestión de Sellos Profesionales
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Los pedidos se crean como "Solicitados" y sólo aparecen para pago una vez que el proveedor los valida en el local.
          </p>
        </div>
        <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
          <Button onClick={() => setShowCargaManual(true)} data-testid="btn-carga-manual-sello"
            className="bg-purple-600 hover:bg-purple-700 text-white w-full sm:w-auto">
            <UserPlus className="w-4 h-4 mr-1" /> Cargar pedido manual
          </Button>
          <Button variant="outline" onClick={() => setShowProveedores(v => !v)} data-testid="btn-toggle-proveedores" className="w-full sm:w-auto">
            {showProveedores ? 'Ocultar' : 'Ver'} proveedores
          </Button>
        </div>
      </div>

      <SelloCargaManualDialog open={showCargaManual} onOpenChange={setShowCargaManual} token={token} onCreado={cargar} />

      {/* Panel de proveedores */}
      {showProveedores && (
        <Card data-testid="card-proveedores">
          <CardHeader>
            <CardTitle className="text-lg">Proveedores de sellos</CardTitle>
            <CardDescription>
              Configurá el/los locales donde se retira. Se usa la dirección del proveedor por default en el WhatsApp de "Listo para retirar".
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {proveedores.map(p => (
              <ProveedorRow
                key={p.id}
                p={p}
                onGuardar={guardarProveedor}
                onBorrar={borrarProveedor}
                onToggle={editarProveedor}
              />
            ))}
            <div className="grid grid-cols-1 md:grid-cols-6 gap-2 items-center pt-2">
              <Input value={nuevoProv.nombre} onChange={(e) => setNuevoProv({ ...nuevoProv, nombre: e.target.value })} placeholder="Nombre" />
              <Input className="md:col-span-2" value={nuevoProv.direccion} onChange={(e) => setNuevoProv({ ...nuevoProv, direccion: e.target.value })} placeholder="Dirección" />
              <Input value={nuevoProv.telefono} onChange={(e) => setNuevoProv({ ...nuevoProv, telefono: e.target.value })} placeholder="Teléfono" />
              <Input value={nuevoProv.pin_confirmacion || ''} onChange={(e) => setNuevoProv({ ...nuevoProv, pin_confirmacion: e.target.value.replace(/\D/g, '').slice(0, 4) })} placeholder="PIN (4 dígitos)" maxLength={4} inputMode="numeric" className="font-mono tracking-widest" data-testid="input-pin-nuevo" />
              <Button onClick={crearProveedor} data-testid="btn-crear-proveedor">
                <Plus className="w-4 h-4 mr-1" /> Agregar
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Tabs */}
      <div className="flex flex-nowrap gap-2 border-b pb-2 scroll-x-safe">
        {TABS.map(t => {
          const Icon = t.icon;
          return (
            <button
              key={t.id || 'all'}
              onClick={() => setTab(t.id)}
              className={`inline-flex items-center gap-1 px-3 py-1.5 rounded text-sm whitespace-nowrap flex-shrink-0 ${
                tab === t.id ? 'bg-purple-600 text-white' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
              }`}
              data-testid={`tab-${t.id || 'todos'}`}
            >
              <Icon className="w-4 h-4" /> {t.label}
            </button>
          );
        })}
      </div>

      {/* Acciones batch */}
      {tab === 'pago_pendiente_colegio' && (
        <div className="flex flex-col sm:flex-row sm:items-center gap-2">
          <Button
            onClick={marcarPagados}
            disabled={marcandoPagados || seleccion.size === 0}
            className="bg-blue-600 hover:bg-blue-700 text-white w-full sm:w-auto"
            data-testid="btn-marcar-pagados"
          >
            {marcandoPagados ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <CircleDollarSign className="w-4 h-4 mr-1" />}
            Marcar {seleccion.size} como PAGADO
          </Button>
          <span className="text-xs text-slate-500">Sólo aparecen aquí los pedidos que ya fueron validados por el proveedor en el local.</span>
        </div>
      )}

      {/* Tabla */}
      <Card>
        <CardContent className="p-0 overflow-x-auto">
          {loading ? (
            <div className="p-6 flex items-center gap-2 text-slate-500"><Loader2 className="w-4 h-4 animate-spin" /> Cargando…</div>
          ) : pedidos.length === 0 ? (
            <div className="p-8 text-center text-slate-500">No hay pedidos en este estado.</div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-xs text-slate-600">
                <tr>
                  {tab === 'pago_pendiente_colegio' && <th className="w-10 py-2"></th>}
                  <th className="py-2 px-3 text-left">Matrícula</th>
                  <th className="py-2 px-3 text-left">Nombre</th>
                  <th className="py-2 px-3 text-left">Evento</th>
                  <th className="py-2 px-3 text-left">Fecha</th>
                  <th className="py-2 px-3 text-left">Estado</th>
                  <th className="py-2 px-3 text-left">WA retiro</th>
                  <th className="py-2 px-3 text-left">Link proveedor</th>
                  <th className="py-2 px-3 text-right">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {pedidos.map(p => {
                  const meta = ESTADO_META[p.estado] || ESTADO_META.solicitado;
                  return (
                    <tr key={p.id} className="border-t hover:bg-slate-50" data-testid={`row-pedido-${p.id}`}>
                      {tab === 'pago_pendiente_colegio' && (
                        <td className="py-2 pl-3">
                          <input
                            type="checkbox"
                            checked={seleccion.has(p.id)}
                            onChange={() => toggleSel(p.id)}
                            data-testid={`chk-${p.id}`}
                          />
                        </td>
                      )}
                      <td className="py-2 px-3 font-mono">{p.matricula || '-'}</td>
                      <td className="py-2 px-3">{p.nombre} {p.apellido}</td>
                      <td className="py-2 px-3 text-slate-600">{p.evento_nombre || '-'}</td>
                      <td className="py-2 px-3 text-slate-500" data-testid={`fecha-${p.id}`}>
                        {(() => {
                          // Mapeo estado-pestaña → campo de fecha correspondiente.
                          const campoPorEstado = {
                            solicitado: 'created_at',
                            pago_pendiente_colegio: 'fecha_validacion_proveedor',
                            pagado: 'fecha_pago',
                            en_produccion: 'fecha_produccion',
                            listo_retiro: 'fecha_listo',
                            entregado: 'fecha_entrega',
                          };
                          const campo = campoPorEstado[tab];
                          let valor = campo ? p[campo] : null;
                          // Fallback 1 · entrada del historial correspondiente al estado activo.
                          if (!valor && Array.isArray(p.historial)) {
                            const entry = p.historial.find(h => h.estado === tab);
                            if (entry?.at) valor = entry.at;
                          }
                          // Fallback 2 · created_at.
                          if (!valor) valor = p.created_at;
                          if (!valor) return '-';
                          try { return new Date(valor).toLocaleString(); }
                          catch { return valor; }
                        })()}
                      </td>
                      <td className="py-2 px-3"><Badge className={`text-xs ${meta.color}`}>{meta.label}</Badge></td>
                      <td className="py-2 px-3">
                        {(() => {
                          const wa = p.whatsapp_listo_retiro;
                          if (!wa) return <span className="text-xs text-slate-400">—</span>;
                          if (wa.status === 'sent') return (
                            <span className="inline-flex items-center gap-1 text-xs text-green-700" title={`SID: ${wa.sid || '—'}\nA: ${wa.to || ''}\n${wa.at ? new Date(wa.at).toLocaleString() : ''}`}>
                              <MessageCircle className="w-3.5 h-3.5" /> Enviado
                            </span>
                          );
                          if (wa.status === 'error') return (
                            <span className="inline-flex items-center gap-1 text-xs text-red-700 cursor-help" title={`Código: ${wa.codigo || '—'}\nMotivo: ${wa.motivo || '—'}\nA: ${wa.to || ''}\n${wa.at ? new Date(wa.at).toLocaleString() : ''}`}>
                              <AlertCircle className="w-3.5 h-3.5" /> Error {wa.codigo || ''}
                            </span>
                          );
                          return (
                            <span className="inline-flex items-center gap-1 text-xs text-slate-500" title={wa.motivo || ''}>
                              <MessageCircle className="w-3.5 h-3.5" /> {wa.status}
                            </span>
                          );
                        })()}
                      </td>
                      <td className="py-2 px-3">
                        <div className="flex items-center gap-1">
                          <Button variant="ghost" size="sm" onClick={() => copiarLink(p.access_token)} title="Copiar link">
                            <CopyIcon className="w-4 h-4" />
                          </Button>
                          <a
                            href={`/sello/${p.access_token}`}
                            target="_blank"
                            rel="noreferrer"
                            className="text-purple-600 hover:text-purple-800"
                            title="Abrir vista proveedor"
                          >
                            <ExternalLink className="w-4 h-4" />
                          </a>
                          {tab === 'pagado' && (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-emerald-700 hover:text-emerald-800 hover:bg-emerald-50"
                              onClick={() => notificarProveedor(p)}
                              disabled={notificandoProv === p.id}
                              title={`Avisar al proveedor por WhatsApp${p.whatsapp_pagado_proveedor?.at ? ' · último envío: ' + new Date(p.whatsapp_pagado_proveedor.at).toLocaleString() : ''}`}
                              data-testid={`btn-notificar-proveedor-${p.id}`}
                            >
                              {notificandoProv === p.id
                                ? <Loader2 className="w-4 h-4 animate-spin" />
                                : <MessageCircle className="w-4 h-4" />}
                            </Button>
                          )}
                        </div>
                      </td>
                      <td className="py-2 px-3 text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-purple-700 hover:text-purple-800 hover:bg-purple-50"
                            onClick={() => reenviarQr(p)}
                            disabled={reenviando === p.id || !p.email}
                            title={p.email ? `Reenviar QR por email a ${p.email}${p.email_qr_enviado_at ? ' · último envío: ' + new Date(p.email_qr_enviado_at).toLocaleString() : ''}` : 'Sin email registrado'}
                            data-testid={`btn-reenviar-qr-${p.id}`}
                          >
                            {reenviando === p.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />}
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            onClick={() => borrarPedido(p)}
                            title="Borrar pedido"
                            data-testid={`btn-borrar-pedido-${p.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}


// Fila editable de proveedor con estado local y botón "Guardar"
function ProveedorRow({ p, onGuardar, onBorrar, onToggle }) {
  const [nombre, setNombre] = useState(p.nombre || '');
  const [direccion, setDireccion] = useState(p.direccion || '');
  const [telefono, setTelefono] = useState(p.telefono || '');
  const [pin, setPin] = useState(p.pin_confirmacion || '');
  const [guardando, setGuardando] = useState(false);

  // Resync si el padre recarga el proveedor
  useEffect(() => {
    setNombre(p.nombre || '');
    setDireccion(p.direccion || '');
    setTelefono(p.telefono || '');
    setPin(p.pin_confirmacion || '');
  }, [p.id, p.nombre, p.direccion, p.telefono, p.pin_confirmacion]);

  const cambiosPendientes =
    nombre !== (p.nombre || '') ||
    direccion !== (p.direccion || '') ||
    telefono !== (p.telefono || '') ||
    pin !== (p.pin_confirmacion || '');

  const pinValido = !pin || /^\d{4}$/.test(pin);

  const handleGuardar = async () => {
    if (!pinValido) {
      toast.error('El PIN debe ser exactamente 4 dígitos numéricos');
      return;
    }
    setGuardando(true);
    const ok = await onGuardar(p.id, {
      nombre: nombre.trim(),
      direccion: direccion.trim(),
      telefono: telefono.trim(),
      pin_confirmacion: pin.trim(),
    });
    setGuardando(false);
    if (!ok) return;
  };

  return (
    <div className="border-b py-3 space-y-2">
      <div className="grid grid-cols-1 md:grid-cols-6 gap-2 items-center">
        <Input value={nombre} onChange={(e) => setNombre(e.target.value)} placeholder="Nombre" data-testid={`input-nombre-${p.id}`} />
        <Input className="md:col-span-2" value={direccion} onChange={(e) => setDireccion(e.target.value)} placeholder="Dirección" data-testid={`input-direccion-${p.id}`} />
        <Input value={telefono} onChange={(e) => setTelefono(e.target.value)} placeholder="Teléfono" data-testid={`input-telefono-${p.id}`} />
        <div className="flex items-center gap-1 text-xs">
          <Switch checked={p.activo} onCheckedChange={(v) => onToggle(p, 'activo', v)} /> Activo
        </div>
        <div className="flex items-center gap-1 text-xs">
          <Switch checked={p.es_default} onCheckedChange={(v) => onToggle(p, 'es_default', v)} /> Default
          <Button variant="ghost" size="sm" className="ml-auto text-red-600" onClick={() => onBorrar(p)} data-testid={`btn-borrar-proveedor-${p.id}`}>
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>
      </div>
      {/* Fila 2: PIN + link al panel público + Guardar */}
      <div className="grid grid-cols-1 md:grid-cols-6 gap-2 items-center pl-2 md:pl-0">
        <div className="flex items-center gap-2">
          <label className="text-xs text-slate-600 whitespace-nowrap">PIN:</label>
          <Input
            type="text"
            inputMode="numeric"
            maxLength={4}
            className={`w-24 font-mono tracking-widest ${pin && !pinValido ? 'border-red-500 focus:ring-red-400' : ''}`}
            value={pin}
            onChange={(e) => setPin(e.target.value.replace(/\D/g, '').slice(0, 4))}
            placeholder="1234"
            data-testid={`input-pin-${p.id}`}
          />
          {pin && !pinValido && (
            <span className="text-xs text-red-600">4 dígitos</span>
          )}
        </div>
        <div className="md:col-span-4 flex items-center gap-2 flex-wrap">
          <span className="text-xs text-slate-500">Panel del proveedor (sin login):</span>
          <code className="text-xs bg-slate-100 px-2 py-1 rounded break-all">
            {p.dashboard_token ? `${window.location.origin}/proveedor-panel/${p.dashboard_token}` : 'Guardá para generar el link'}
          </code>
          {p.dashboard_token && (
            <>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  navigator.clipboard.writeText(`${window.location.origin}/proveedor-panel/${p.dashboard_token}`);
                  toast.success('Link del panel copiado');
                }}
                data-testid={`btn-copiar-panel-${p.id}`}
                title="Copiar link"
              >
                <CopyIcon className="w-4 h-4" />
              </Button>
              <a
                href={`/proveedor-panel/${p.dashboard_token}`}
                target="_blank"
                rel="noreferrer"
                className="text-purple-600 hover:text-purple-800 inline-flex items-center"
                title="Abrir panel"
              >
                <ExternalLink className="w-4 h-4" />
              </a>
            </>
          )}
        </div>
        <Button
          onClick={handleGuardar}
          disabled={!cambiosPendientes || !pinValido || guardando}
          className={cambiosPendientes ? 'bg-green-600 hover:bg-green-700 text-white' : ''}
          variant={cambiosPendientes ? 'default' : 'outline'}
          size="sm"
          data-testid={`btn-guardar-proveedor-${p.id}`}
        >
          {guardando ? 'Guardando…' : cambiosPendientes ? 'Guardar' : 'Guardado'}
        </Button>
      </div>
    </div>
  );
}

